<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/modules/user/stores/userStore'
import { usePayStore } from '@/modules/pay/store/payStore'
import { formatDate } from '@/utils/dateFormatters'
import { copyToClipboard } from '@/utils/CopyToClipboard'
import { useI18n } from 'vue-i18n'

import SlideOutInterfaceRenewSubscribe from '@/components/slide-out-interface/SlideOutInterfaceRenewSubscribe.vue'

const userStore = useUserStore()
const payStore = usePayStore()
const { t, locale } = useI18n()

const isSheetRenewSubscribeOpen = ref(false)

const actualExpiryTime = computed<number | null>(() => userStore.user?.lastTariffExpiresAt ?? null)

const daysLeft = computed(() => {
  const nowMs = Date.now()
  const expiry = actualExpiryTime.value
  if (expiry != null) {
    const diffMs = expiry - nowMs
    return Math.max(Math.ceil(diffMs / (1000 * 60 * 60 * 24)), 0)
  }
  return 0
})

const tariffInfo = computed(() => {
  const expiry = actualExpiryTime.value
  if (expiry == null) {
    return { status: t('common.loading'), logo: '/icons/status/red.svg', showTime: false }
  }
  if (daysLeft.value > 3) {
    return { status: t('tariffs.tariffConnectedUntil'), logo: '/icons/status/green.svg', showTime: false }
  } else if (daysLeft.value > 0 && Date.now() < expiry) {
    return { status: t('tariffs.tariffWillExpire'), logo: '/icons/status/yellow.svg', showTime: true }
  } else {
    return { status: t('tariffs.tariffExpired'), logo: '/icons/status/red.svg', showTime: true }
  }
})

const formattedDate = computed(() =>
  actualExpiryTime.value ? formatDate(actualExpiryTime.value, tariffInfo.value.showTime, locale.value) : '',
)

const hasActiveAutoRenew = computed(() => {
  if (payStore.isSavedPaymentMethodsLoaded) {
    return payStore.hasActiveAutoRenew
  }
  return Boolean(userStore.user?.nextChargeAt)
})

const renewButtonText = computed(() =>
  hasActiveAutoRenew.value ? t('tariffs.renewManually') : t('tariffs.renewSubscription'),
)

onMounted(async () => {
  if (!payStore.isSavedPaymentMethodsLoaded) {
    await payStore.loadSavedPaymentMethods(false)
  }
})
</script>

<template>
  <div class="d-flex flex-column ga-2">
    <p class="d-flex align-center ga-1">
      <span class="text-body-1 text-text-disabled">{{ t('common.loginLabel') }}</span>
      <span
        class="text-body-1 cursor-pointer"
        @click="copyToClipboard(userStore.user?.email || userStore.user?.telegramID || '')"
      >
        {{ userStore.user?.email || userStore.user?.telegramID }}
      </span>
    </p>

    <div class="d-flex align-center ga-1">
      <div
        :style="{
          backgroundImage: `url(${tariffInfo.logo})`,
          width: '16px',
          height: '16px',
          backgroundSize: 'contain',
        }"
      />

      <div class="text-body-1">{{ tariffInfo.status }}</div>

      <div class="text-subtitle-1">{{ formattedDate }}</div>
    </div>
  </div>

  <div class="interface-button">
    <v-btn
      variant="flat"
      color="primary"
      rounded="lg"
      block
      type="submit"
      @click="isSheetRenewSubscribeOpen = true"
    >
      {{ renewButtonText }}
    </v-btn>
  </div>

  <slide-out-interface-renew-subscribe
    :isRenewSheetOpen="isSheetRenewSubscribeOpen"
    :buttonText="renewButtonText"
    @close="isSheetRenewSubscribeOpen = false"
  />
</template>
